import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { deepComparison } from "client/Misc/compareUtils";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import { AutotaskConnectionSelectOptionsModel, getAutotaskConnections } from "client/Api/Apps/EditApp/appParameters";
import { AutotaskConnectionListItemModel } from "client/Api/AutotaskConfiguration/autotaskConfiguration";
import { AutotaskConnectionDialog } from "client/Features/Settings/Connections/Autotask/AutotaskConnectionDialog";
import {
	SaveAutotaskAppModel,
	EditAutotaskAppViewModel,
	AutotaskConnectionIdentifierViewModel,
	saveAutotaskApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveAutotaskAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("connection")
	.add(
		isNotNullNorUndefined()
	);

export type EditAutotaskAppProps = {
	initialSettings: EditAppSettings<EditAutotaskAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditAutotaskApp = forwardRef(
	(
		props: EditAutotaskAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const alertManager: AlertManager = useAlerts();
		const globalCancellation: AbortSignal = useUnmountedCancellation();
		const [formData, setFormData] = useState<SaveAutotaskAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const [optionsModel, setOptionsModel] = useState<AutotaskConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const [isEditDialogVisible, setIsEditDialogVisible] = useState<boolean>(false);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveAutotaskAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const connectionOptions: readonly SelectItem<AutotaskConnectionIdentifierViewModel>[] = useMemo(
			(): readonly SelectItem<AutotaskConnectionIdentifierViewModel>[] => {
				return optionsModel.options
					.map(
						o => ({
							value: {
								id: o.id
							},
							text: o.name
						})
					);
			},
			[optionsModel.options]
		);

		const loadAutotaskConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const optionsModel: AutotaskConnectionSelectOptionsModel = await getAutotaskConnections(
						signal
					);

					setOptionsModel(optionsModel);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		const addConnectionHandler = useCallback(
			(): void => {
				setIsEditDialogVisible(true);
			},
			[]
		);

		const afterSaveHandler = async (item: AutotaskConnectionListItemModel): Promise<void> => {
			await loadAutotaskConnections(globalCancellation);
			formHandler.for("connection")
				.attachToSelect()
				.onChange(item);
		};

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadAutotaskConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[loadAutotaskConnections]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveAutotaskAppModel>(
			formHandler,
			saveAutotaskApp,
			AppType.Autotask
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connection", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem label={localize("_Connection")}>
					<InputRow>
						<InputRow.Stretch>
							<Select
								{...formHandler.for("connection").attachToSelect()}
								options={connectionOptions}
								areEqual={deepComparison}
								placeholder={localize("_SelectConnection")}
								menuTitle={localize("_Connection")}
							/>
						</InputRow.Stretch>
						{optionsModel.canCreateNew && (
							<Button
								text={localize("_Add")}
								onClick={addConnectionHandler}
								kind="secondary"
							/>
						)}
					</InputRow>
					<ValidationError
						{...formHandler.for("connection").attachToValidationError()}
					/>
				</FormItem>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{isEditDialogVisible && (
					<AutotaskConnectionDialog
						onAfterSave={(item) => afterSaveHandler(item)}
						onClose={() => setIsEditDialogVisible(false)}
						connectionId={undefined}
					/>
				)}
			</EditAppBase>
		);
	}
);
EditAutotaskApp.displayName = "EditAutotaskApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditAutotaskAppViewModel>
): SaveAutotaskAppModel {
	const {
		destinationWindow,
		connection
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		connection
	};
}